import { Fragment, FC } from "react";
import { Popover, Transition } from "@headlessui/react";
import { UserPlusIcon } from "@heroicons/react/24/outline";
import { GuestsObject } from "data/interfaces";
import { useSelector, useDispatch } from "react-redux";
import { selectGuests, setGuestsAdults, setGuestsChildren, setGuestsInfants, setGuests } from "application/features/guests/guestsSlice";
import ClearDataButton from "components/Buttons/ClearDataButton";
import NcInputNumber from "components/NcInputNumber/NcInputNumber";

export interface GuestsInputProps {
  className?: string;
  showButtonInside?: boolean;
  onChange?: (guests: GuestsObject) => void;
}

const GuestsInput: FC<GuestsInputProps> = ({
  className = "flex-1",
  showButtonInside = false,
  onChange = () => {}
}) => {

  const guests: GuestsObject = useSelector(selectGuests);
  const dispatch = useDispatch();

  const handleChangeData = (value: number, type: keyof GuestsObject) => {
    if (type === "adults") {
      dispatch(setGuestsAdults(value));
    }
    if (type === "children") {
      dispatch(setGuestsChildren(value));
    }
    if (type === "infants") {
      dispatch(setGuestsInfants(value));
    }
    onChange({
      adults: type === "adults" ? value : guests.adults,
      children: type === "children" ? value : guests.children,
      infants: type === "infants" ? value : guests.infants,
    });
  };

  const totalGuests =
    guests.children + guests.adults + guests.infants;

  return (
    <Popover className={`flex relative ${className}`}>
      {({ open }) => (
        <>
          <div
            className={`flex-1 flex items-center focus:outline-none rounded-b-3xl ${open? "shadow-lg" : ""}`}
          >
            <Popover.Button
              className={`relative z-10 flex-1 flex text-left items-center p-3 space-x-3 focus:outline-none`}
            >
              <div className="text-neutral-300 dark:text-neutral-400">
                <UserPlusIcon className="w-5 h-5 lg:w-7 lg:h-7" />
              </div>
              <div className="flex-grow">
                <span className="block xl:text-lg font-semibold">
                  {totalGuests || ""} Guests
                </span>
                <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
                  {totalGuests ? "Guests" : "Add guests"}
                </span>
              </div>

              {!!totalGuests && open && (
                <ClearDataButton
                  onClick={() => {
                    dispatch(setGuests({ adults: 0, children: 0, infants: 0 }));
                  }}
                />
              )}
            </Popover.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 z-10 w-full sm:min-w-[340px] max-w-sm bg-white dark:bg-neutral-800 top-full mt-3 py-5 sm:py-6 px-4 sm:px-8 rounded-3xl shadow-xl ring-1 ring-black ring-opacity-5 ">
              <NcInputNumber
                className="w-full"
                defaultValue={guests.adults}
                onChange={(value) => handleChangeData(value, "adults")}
                max={10}
                min={1}
                label="Adults"
                desc="Ages 13 or above"
              />
              <NcInputNumber
                className="w-full mt-6"
                defaultValue={guests.children}
                onChange={(value) => handleChangeData(value, "children")}
                max={4}
                label="Children"
                desc="Ages 2 - 12"
              />

              <NcInputNumber
                className="w-full mt-6"
                defaultValue={guests.infants}
                onChange={(value) => handleChangeData(value, "infants")}
                max={4}
                label="Infants"
                desc="Ages 0 - 2"
              />
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default GuestsInput;
